import { Checkbox } from '@globalmed/atlas-ui';
import { type ComponentProps, memo, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { FormBuilderLabel } from '../FormBuilderLabel';
import styles from './FormBuilderCheckBox.module.css';

type TProps = ComponentProps<typeof Checkbox> & {
  label: string;
  onLabelChange: (label: string) => void;
  error?: boolean;
};

export const FormBuilderCheckBox =
  /**
   * Checkbox component for form builder with editable label
   * @param {TProps} props - Component props
   * @param {string} props.label - Checkbox label text
   * @param {Function} props.onLabelChange - Callback when label changes
   * @param {boolean} [props.error=false] - Whether the field has validation errors
   * @returns {JSX.Element} Form builder checkbox component
   */
  memo(({ label, onLabelChange, error = false, ...checkProps }: TProps) => {
    const { t } = useTranslation();
    const checkboxId = useId();

    return (
      <div className={styles.container}>
        <Checkbox id={checkboxId} sx={{ marginTop: '4px' }} {...checkProps} />

        <FormBuilderLabel
          label={label}
          htmlFor={checkboxId}
          onLabelChange={onLabelChange}
          error={error}
          sx={{
            fontSize: '16px',
            fontWeight: '500',
            lineHeight: '24px',
          }}
          placeholder={t('form-builder.checkbox-label')}
        />
      </div>
    );
  });

FormBuilderCheckBox.displayName = 'FormBuilderCheckBox';
